import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const ActivePlanCard = ({ plan, onStartReading, onViewProgress }) => {
  const progressPercentage = plan?.progress || 0;
  const planName = plan?.reading_plans?.name || 'Reading Plan';
  const currentDay = plan?.current_day || 1;
  const totalDays = plan?.reading_plans?.duration_days || 365;
  const streakDays = plan?.streakDays || 0;
  const completedReferences = plan?.completedReferences || 0;
  const totalReferences = plan?.totalReferences || 0;
  const nextReadingRefs = plan?.nextReading?.references || [];
  const estimatedTime = plan?.reading_plans?.estimated_daily_reading_time || 15;

  // Calculate expected pace (where user should be based on days elapsed)
  const startDate = plan?.start_date ? new Date(plan.start_date) : new Date();
  const today = new Date();
  const daysElapsed = Math.max(1, Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1);
  const expectedProgress = Math.min(100, (daysElapsed / totalDays) * 100);
  const isAhead = progressPercentage >= expectedProgress;
  const paceDiff = Math.abs(progressPercentage - expectedProgress)?.toFixed(1);

  const getStreakColor = (streak) => {
    if (streak >= 30) return 'text-success';
    if (streak >= 14) return 'text-warning';
    if (streak >= 7) return 'text-accent';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 hover:shadow-warm transition-all duration-200">
      {/* Plan Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-foreground text-base leading-tight truncate">
            {planName}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Day {currentDay} of {totalDays}
          </p>
        </div>

        {/* Streak Indicator */}
        {streakDays > 0 && (
          <div className="flex items-center space-x-1 ml-2 shrink-0">
            <Icon name="Flame" size={14} color={
              getStreakColor(streakDays) === 'text-success' ? 'var(--color-success)' :
              getStreakColor(streakDays) === 'text-warning' ? 'var(--color-warning)' :
              getStreakColor(streakDays) === 'text-accent' ? 'var(--color-accent)' :
              'var(--color-muted-foreground)'
            } />
            <span className={cn("text-xs font-medium", getStreakColor(streakDays))}>
              {streakDays}d
            </span>
          </div>
        )}
      </div>

      {/* Overlapping Progress Graph */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-muted-foreground font-medium">Progress</span>
          <span className={cn("font-semibold text-xs", isAhead ? "text-success" : "text-warning")}>
            {isAhead ? `+${paceDiff}% ahead` : `-${paceDiff}% behind`} pace
          </span>
        </div>

        {/* Overlapping bar: expected pace (background) + actual progress (foreground) */}
        <div className="relative w-full h-3 rounded-full overflow-hidden bg-muted">
          {/* Expected pace — background shadow layer */}
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-muted-foreground/30 transition-all duration-300"
            style={{ width: `${Math.min(expectedProgress, 100)}%` }}
          />
          {/* Actual progress — foreground layer */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 rounded-full transition-all duration-300",
              isAhead ? "bg-success" : "bg-primary"
            )}
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>

        {/* Legend */}
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className={cn("inline-block w-2 h-1.5 rounded-sm", isAhead ? "bg-success" : "bg-primary")}></span>
              Your progress ({progressPercentage?.toFixed(1)}%)
            </span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="inline-block w-2 h-1.5 rounded-sm bg-muted-foreground/40"></span>
              Expected ({expectedProgress?.toFixed(1)}%)
            </span>
          </div>
        </div>

        {/* Readings count */}
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {completedReferences} of {totalReferences} readings completed
        </p>
      </div>

      {/* Today's Reading - compact */}
      {nextReadingRefs?.length > 0 && (
        <div className="bg-muted/30 rounded-lg px-3 py-2 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1.5">
              <Icon name="BookOpen" size={13} color="var(--color-primary)" />
              <span className="text-xs font-medium text-foreground">Today</span>
            </div>
            <span className="text-[10px] text-muted-foreground">~{estimatedTime} min</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {nextReadingRefs?.slice(0, 2)?.join(', ')}
            {nextReadingRefs?.length > 2 && ` +${nextReadingRefs?.length - 2} more`}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={() => onStartReading?.(plan)}
          className="flex-1 rounded-full"
          size="sm"
        >
          <Icon name="BookOpen" size={13} />
          Continue Reading
        </Button>
        <Button
          onClick={() => onViewProgress?.(plan)}
          variant="outline"
          size="sm"
          className="rounded-full"
        >
          <Icon name="BarChart" size={13} />
        </Button>
      </div>
    </div>
  );
};

export default ActivePlanCard;